import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Drawer,
  Empty,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import {
  CloseOutlined,
  DownloadOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import moment from "moment";

import {
  downloadZatcaInvoiceXml,
  getSingleZatcaInvoice,
  reportZatcaInvoice,
  retryZatcaInvoice,
} from "../../../network/network";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import { AppContext } from "../../../context/AppContextProvider";
import {
  UserType,
  ZATCA_INVOICE_RETRY_MAX,
  ZatcaInvoiceStatus,
  ZatcaInvoiceSubtype,
  zatcaInvoiceStatuses,
  zatcaDocumentTypes,
  zatcaInvoiceSubtypes,
  zatcaResponseStatuses,
} from "../../../configs/constants";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import { handleApiError } from "../../../utils/errorHandler";

const { Text, Paragraph } = Typography;

const getInvoiceFromResponse = (response) => {
  return (
    response?.data?.data?.zatca_invoice ||
    response?.data?.zatca_invoice ||
    response?.data?.data ||
    null
  );
};

const getOption = (options, id) =>
  options.find(
    (item) => item.id === id || item.value === id || item.name === id,
  );

const getZatcaValue = (value) => {
  if (!value) return null;

  if (typeof value === "object") {
    return value.value || value.id || value.name || value.key;
  }

  return value;
};

const getOptionTag = (options, value, t) => {
  const id = getZatcaValue(value);
  const option = getOption(options, id);

  if (option) {
    return <Tag color={option.color}>{t(option.text)}</Tag>;
  }

  if (value?.label) {
    return <Tag>{value.label}</Tag>;
  }

  return id ? <Tag>{t(id)}</Tag> : null;
};

const formatDateTime = (date) => {
  if (!date) return "-";

  const parsedDate = moment(date);

  return parsedDate.isValid() ? parsedDate.format("MMM. D, YYYY hh:mm a") : "-";
};

const FieldRow = ({ label, value, children }) => (
  <Col xs={24} sm={12}>
    <div style={{ fontSize: 12, color: "var(--color-text)", marginBottom: 4 }}>
      {label}
    </div>

    <div
      style={{
        fontSize: 14,
        fontWeight: 500,
        wordBreak: "break-word",
      }}
    >
      {children ??
        (value === undefined || value === null || value === "" ? "-" : value)}
    </div>
  </Col>
);

const ErrorOrWarningCard = ({ item, kind, t }) => {
  const isError = kind === "error";

  return (
    <Alert
      type={isError ? "error" : "warning"}
      showIcon
      style={{ marginBottom: 8 }}
      message={item?.code || (isError ? t("error") : t("warning"))}
      description={item?.message || "-"}
    />
  );
};

const InvoiceDetailDrawer = ({
  open,
  transactionId,
  invoiceData = null,
  onClose,
  onRetried,
}) => {
  const { authUser, appConfig } = useContext(AppContext);
  const { t } = useTranslation();

  const [invoice, setInvoice] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [retryLoading, setRetryLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [xmlLoading, setXmlLoading] = useState(false);

  useEffect(() => {
    if (!open) return;

    if (!transactionId) {
      setInvoice(invoiceData);
      setIsLoading(false);
      return;
    }

    let isEffectActive = true;

    setIsLoading(true);
    setInvoice(invoiceData);

    getSingleZatcaInvoice(transactionId)
      .then((response) => {
        if (!isEffectActive) return;

        if (response?.data?.success === false) {
          setInvoice(invoiceData);
          handleApiError(response, t);
          return;
        }

        setInvoice(getInvoiceFromResponse(response) || invoiceData);
      })
      .catch((error) => {
        if (!isEffectActive) return;

        setInvoice(invoiceData);
        handleApiError(error, t);
      })
      .finally(() => {
        if (!isEffectActive) return;

        setIsLoading(false);
      });

    return () => {
      isEffectActive = false;
    };
  }, [open, transactionId, invoiceData, t]);

  const errors = Array.isArray(invoice?.errors) ? invoice.errors : [];
  const warnings = Array.isArray(invoice?.warnings) ? invoice.warnings : [];

  const invoiceStatus = getZatcaValue(
    invoice?.status_details || invoice?.status,
  );
  const invoiceSubtype = getZatcaValue(
    invoice?.invoice_subtype_details || invoice?.invoice_subtype,
  );

  const isRejected = invoiceStatus === ZatcaInvoiceStatus.Rejected;
  const isPending = invoiceStatus === ZatcaInvoiceStatus.Pending;
  const isFailed = invoiceStatus === ZatcaInvoiceStatus.Failed;
  const isOwner = authUser?.type === UserType.BusinessOwner;

  const canReportClearInvoice =
    isOwner && invoice?.id && (isPending || isFailed);

  const isStandardInvoice = invoiceSubtype === ZatcaInvoiceSubtype.Standard;
  const reportClearButtonText = isStandardInvoice
    ? t("clear_invoice")
    : t("report_invoice");

  const canManualRetry =
    invoice?.can_retry === true ||
    (isFailed && Number(invoice?.retry_count || 0) >= ZATCA_INVOICE_RETRY_MAX);

  const handleRetry = () => {
    if (!transactionId) return;

    setRetryLoading(true);

    retryZatcaInvoice(transactionId)
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        NotificationWithIcon("success", t("zatca_invoice_retry_success"));
        onRetried?.();
        onClose?.();
      })
      .catch((error) => {
        const updatedInvoice = getInvoiceFromResponse(error?.response);

        if (updatedInvoice) {
          setInvoice((prev) => ({
            ...prev,
            ...updatedInvoice,
          }));
          onRetried?.();
        }

        if (error?.response?.status === 503) {
          NotificationWithIcon(
            "warning",
            t("zatca_temporarily_unavailable"),
            error?.response?.data?.message || t("zatca_invoice_retry_failed"),
          );
        } else {
          handleApiError(error, t);
        }
      })
      .finally(() => {
        setRetryLoading(false);
      });
  };

  const handleReportClearInvoice = () => {
    if (!invoice?.id) return;

    setReportLoading(true);

    reportZatcaInvoice(invoice.id)
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        const updatedInvoice = getInvoiceFromResponse(response);

        if (updatedInvoice) {
          setInvoice((prev) => ({
            ...prev,
            ...updatedInvoice,
          }));
        }

        NotificationWithIcon(
          "success",
          t("zatca_invoice_report_success"),
          response?.data?.message || t("invoice_submitted_to_zatca"),
        );

        onRetried?.();
      })
      .catch((error) => {
        const updatedInvoice = getInvoiceFromResponse(error?.response);

        if (updatedInvoice) {
          setInvoice((prev) => ({
            ...prev,
            ...updatedInvoice,
          }));
          onRetried?.();
        }

        if (error?.response?.status === 503) {
          NotificationWithIcon(
            "warning",
            t("zatca_invoice_report_failed"),
            error?.response?.data?.message || t("something_went_wrong"),
          );
        } else {
          handleApiError(error, t);
        }
      })
      .finally(() => {
        setReportLoading(false);
      });
  };

  const handleDownloadXml = () => {
    if (!transactionId) return;

    setXmlLoading(true);

    const filename = `zatca-transaction-${transactionId}.xml`;

    downloadZatcaInvoiceXml(transactionId, filename)
      .catch((error) => {
        handleApiError(error, t);
      })
      .finally(() => {
        setXmlLoading(false);
      });
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={680}
      title={t("invoice_detail")}
      placement={appConfig.direction === "rtl" ? "left" : "right"}
      destroyOnClose
      closable={false}
      extra={
        <Button type="text" danger icon={<CloseOutlined />} onClick={onClose} />
      }
    >
      <Spin spinning={isLoading}>
        {!invoice && !isLoading ? (
          <Empty />
        ) : invoice ? (
          <>
            <Card className="app-mb-16">
              <Space size="small" wrap>
                {getOptionTag(
                  zatcaInvoiceStatuses,
                  invoice.status_details || invoice.status,
                  t,
                )}
                {getOptionTag(
                  zatcaDocumentTypes,
                  invoice.document_type_details || invoice.document_type,
                  t,
                )}
                {getOptionTag(
                  zatcaInvoiceSubtypes,
                  invoice.invoice_subtype_details || invoice.invoice_subtype,
                  t,
                )}
                {getOptionTag(
                  zatcaResponseStatuses,
                  invoice.response_status_details || invoice.response_status,
                  t,
                )}
              </Space>
            </Card>

            {isRejected && (
              <Alert
                type="error"
                showIcon
                className="app-mb-16"
                message={t("zatca_invoice_rejected_alert")}
              />
            )}

            {isFailed && !canManualRetry && invoice?.next_retry_at && (
              <Alert
                type="info"
                showIcon
                className="app-mb-16"
                message={t("zatca_invoice_retry_attempt", {
                  minutes: Math.max(
                    1,
                    moment(invoice.next_retry_at).diff(moment(), "minutes"),
                  ),
                  n: invoice.retry_count || 0,
                  max: ZATCA_INVOICE_RETRY_MAX,
                })}
              />
            )}

            <Card
              title={t("invoice_summary")}
              className="app-mb-16"
              bodyStyle={{ paddingTop: 12 }}
            >
              <Row gutter={[16, 16]}>
                <FieldRow
                  label={t("transaction_id")}
                  value={invoice.transaction_id || invoice?.transaction?.id}
                />

                <FieldRow label={t("uuid")} value={invoice.uuid} />

                <FieldRow
                  label={t("branch")}
                  value={
                    invoice?.branch?.name ||
                    invoice?.branch_name ||
                    invoice?.branch_id
                  }
                />

                <FieldRow
                  label={t("device_name")}
                  value={invoice?.device?.device_name}
                />

                <FieldRow
                  label={t("cr_number")}
                  value={
                    invoice?.cr ||
                    invoice?.cr_number ||
                    invoice?.zatca_cr_profile?.cr ||
                    invoice?.branch?.cr
                  }
                />

                <FieldRow label={t("icv")} value={invoice.icv} />

                <FieldRow
                  label={t("document_type")}
                  value={invoice?.document_type_details?.label}
                />

                <FieldRow
                  label={t("subtype")}
                  value={invoice?.invoice_subtype_details?.label}
                />

                <FieldRow
                  label={t("response_status")}
                  value={
                    invoice?.response_status_details?.label ||
                    invoice?.status_details?.label ||
                    invoice?.response_status
                  }
                />

                <FieldRow
                  label={t("submitted_at")}
                  value={formatDateTime(invoice.submitted_at)}
                />

                <FieldRow
                  label={t("responded_at")}
                  value={formatDateTime(invoice.responded_at)}
                />
              </Row>
            </Card>

            <Card
              title={t("amount_summary")}
              className="app-mb-16"
              bodyStyle={{ paddingTop: 12 }}
            >
              <Row gutter={[16, 16]}>
                <FieldRow label={t("total_without_tax")}>
                  <CurrencyFormatter value={invoice.total_excluding_vat} />
                </FieldRow>

                <FieldRow label={t("vat")}>
                  <CurrencyFormatter value={invoice.total_vat} />
                </FieldRow>

                <FieldRow label={t("total_with_tax")}>
                  <CurrencyFormatter value={invoice.total_including_vat} />
                </FieldRow>
              </Row>
            </Card>

            <Card
              title={t("zatca_errors")}
              className="app-mb-16"
              bodyStyle={{ paddingTop: 12 }}
            >
              {errors.length === 0 ? (
                <Text type="secondary">{t("no_errors")}</Text>
              ) : (
                errors.map((err, index) => (
                  <ErrorOrWarningCard
                    key={`err-${index}`}
                    item={err}
                    kind="error"
                    t={t}
                  />
                ))
              )}
            </Card>

            <Card
              title={t("warnings")}
              className="app-mb-16"
              bodyStyle={{ paddingTop: 12 }}
            >
              {warnings.length === 0 ? (
                <Text type="secondary">{t("no_warnings")}</Text>
              ) : (
                warnings.map((warning, index) => (
                  <ErrorOrWarningCard
                    key={`warning-${index}`}
                    item={warning}
                    kind="warning"
                    t={t}
                  />
                ))
              )}
            </Card>

            <Space wrap>
              {canReportClearInvoice && (
                <Button
                  type="primary"
                  loading={reportLoading}
                  onClick={handleReportClearInvoice}
                >
                  {reportClearButtonText}
                </Button>
              )}

              <Button type="default"
                icon={<DownloadOutlined />}
                loading={xmlLoading}
                onClick={handleDownloadXml}
                disabled={!transactionId || !invoice?.xml_url}
              >
                {t("download_xml")}
              </Button>

              {(isFailed || isRejected) && (
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  loading={retryLoading}
                  disabled={!canManualRetry}
                  onClick={handleRetry}
                >
                  {t("retry")}
                </Button>
              )}
            </Space>

            {!canManualRetry && isFailed && (
              <Paragraph
                type="secondary"
                style={{ fontSize: 12, marginTop: 12 }}
              >
                {t("zatca_invoice_retry_disabled_helper", {
                  max: ZATCA_INVOICE_RETRY_MAX,
                })}
              </Paragraph>
            )}
          </>
        ) : null}
      </Spin>
    </Drawer>
  );
};

export default InvoiceDetailDrawer;
